import assertNever from 'assert-never';
import {
  ActionActor,
  AutomationActionTemplate,
  AutomationActionType,
  ExperienceTemplateMessage,
  IDocuSignEnvelopeTemplate,
  IExperienceTemplate,
  InternalStorageInstructionType,
  ISupportingActor,
  ITaskTemplate,
  PARTICIPANT_ACTOR_KEY,
  QuestionType,
  SubTaskDataDestinationSupportingActor,
  SubTaskType,
  SupportingActorType,
  TaskBlockTemplate,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { analyzeExperienceInterpolationsInBlocks } from '../set/interpolation';
import { getAllMessageTemplates } from './template';

// ⚠️ DUPLICATED, ON PURPOSE. A behavioural twin of this module lives in the
// BE repo at new-main/packages/shared/src/services/experiences/actorUsage.ts
// (published as @dadohr/shared/services/experiences/actorUsage) so the AI/MCP
// tools can use it. The two can't share one home: this FE copy leans on apps'
// own FE-safe interpolation, while the shared copy uses shared's interpolation
// (which pulls in firebase-admin and so can never enter the FE bundle), and
// apps consumes @dadohr/shared as a published package that exposes only
// domainTypes, not services.
//
// If you change the behaviour of any function here — a new usage site, a new
// automation action, different selectability rules — make the SAME change in
// the shared copy, and keep both test suites in lockstep.

export const findActorKeyCollision = (
  actors: ISupportingActor[],
  candidateKey: string,
  excludeKey?: string
): ISupportingActor | null => {
  return (
    actors.find((a) => a.key === candidateKey && a.key !== excludeKey) || null
  );
};

// 'participant' is a reserved pseudo-actor, and 'experience'/'org' are
// built-in interpolation prefixes (see analyzeExperienceInterpolation) —
// none of these are ever present in a template's supportingActors map, so
// findActorKeyCollision alone can't catch a new actor's key aliasing one.
const RESERVED_ACTOR_KEYS: ReadonlySet<string> = new Set([
  PARTICIPANT_ACTOR_KEY,
  'experience',
  'org'
]);

export const isReservedActorKey = (candidateKey: string): boolean =>
  RESERVED_ACTOR_KEYS.has(candidateKey);

// Use for pickers/dropdowns — archived actors shouldn't be newly assignable.
// Pass includeChannelActors: false for pickers that need to resolve to a
// real person (email, user profile, etc) — a channel actor has no such
// identity behind it.
export const getSelectableSupportingActors = (
  supportingActors: { [key: string]: ISupportingActor },
  options?: { includeChannelActors?: boolean }
): ISupportingActor[] => {
  const includeChannelActors = options?.includeChannelActors ?? true;
  return Object.values(supportingActors).filter(
    (a) =>
      !a.archived &&
      (includeChannelActors || a.type !== SupportingActorType.CHANNEL)
  );
};

// Use to resolve an actor a template already references (e.g. an existing
// selection's label/color) — resolves archived actors too, so old
// references still render a name instead of going blank.
export const getSupportingActorForDisplay = (
  supportingActors: { [key: string]: ISupportingActor },
  key: string
): ISupportingActor | null =>
  Object.prototype.hasOwnProperty.call(supportingActors, key)
    ? supportingActors[key]
    : null;

// True for an empty selection (not yet made) or one resolving to a
// selectable (non-archived, non-channel) actor; false for an archived,
// channel, or deleted actor reference — a channel actor never resolves to a
// real user, so a mapping to/from one can never do anything.
export const isSupportingActorMappingValid = (
  supportingActors: { [key: string]: ISupportingActor },
  key: string
): boolean => {
  if (!key) return true;
  const actor = getSupportingActorForDisplay(supportingActors, key);
  return (
    !!actor &&
    !actor.archived &&
    actor.type !== SupportingActorType.CHANNEL
  );
};

export type ActorUsageRef = {
  itemType: 'TASK' | 'MESSAGE' | 'SUBTASK' | 'AUTOMATION' | 'CONTENT';
  itemTitle: string;
  detail: string;
};

const actorActorMatches = (
  actor: ActionActor | null | undefined,
  actorKey: string
): boolean => !!actor && actor.type === 'actor' && actor.key === actorKey;

// receivesCopy isn't declared on ITaskTemplate/ExperienceTemplateMessage —
// it's read defensively at every existing call site (e.g. Assignment.tsx),
// and a role entry can be a bare string or { key, emailField }.
const hasReceivesCopyRole = (item: unknown, actorKey: string): boolean => {
  const roles = (item as { receivesCopy?: { roles?: unknown[] } })
    ?.receivesCopy?.roles;
  return !!roles?.some(
    (r) => (typeof r === 'string' ? r : (r as { key: string }).key) === actorKey
  );
};

const messageTitle = (message: ExperienceTemplateMessage): string =>
  message.subject || 'Untitled message';

const findContentVariableUsage = (
  blocks: TaskBlockTemplate[],
  actorKey: string
): boolean => !!analyzeExperienceInterpolationsInBlocks(blocks).actorVariables[actorKey];

const findDocuSignResource = (
  tpl: IExperienceTemplate,
  resourceId: string
): IDocuSignEnvelopeTemplate | undefined =>
  tpl.resources.find(
    (r): r is IDocuSignEnvelopeTemplate =>
      r.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE &&
      r.resourceId === resourceId
  );

const findTaskSubtaskUsages = (
  tpl: IExperienceTemplate,
  task: ITaskTemplate,
  actorKey: string
): ActorUsageRef[] => {
  const usages: ActorUsageRef[] = [];
  for (const block of task.blocks) {
    if (
      block.type === SubTaskType.CREATE_MEETING &&
      block.data.pplToAdd.some((a) => actorActorMatches(a, actorKey))
    ) {
      usages.push({
        itemType: 'SUBTASK',
        itemTitle: task.title,
        detail: 'Attendee for Create Calendar Event subtask'
      });
    }
    if (block.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN) {
      const resource = findDocuSignResource(tpl, block.data.resourceId);
      if (resource?.signers.some((s) => s.actor.key === actorKey)) {
        usages.push({
          itemType: 'SUBTASK',
          itemTitle: task.title,
          detail: 'Document signer'
        });
      }
      if (resource?.ccs.some((s) => s.actor.key === actorKey)) {
        usages.push({
          itemType: 'SUBTASK',
          itemTitle: task.title,
          detail: 'Document recipient'
        });
      }
    }
    if (
      block.type === SubTaskType.QUESTION &&
      block.data.question.type === QuestionType.SUPPORTING_ACTOR
    ) {
      // The assigned actor key lives on the storages[] data destination, not
      // directly on the question — that's where the UI actually persists it.
      const assignment = block.data.storages.find(
        (s): s is SubTaskDataDestinationSupportingActor =>
          s.type === InternalStorageInstructionType.SUPPORTING_ACTOR
      );
      if (assignment?.supportingActorToAssign === actorKey) {
        usages.push({
          itemType: 'SUBTASK',
          itemTitle: task.title,
          detail: 'Assign Supporting Actor subtask'
        });
      }
    }
  }
  return usages;
};

// The 8 action types below all carry the same `pplToAdd: ActionActor[]`
// field and behave identically here — checked via a type guard instead of
// 8 separate switch cases, so this map is the single place their labels
// are enumerated (a missing entry is a compile error, not a silent gap).
type ActionWithPplToAdd = Extract<
  AutomationActionTemplate,
  { pplToAdd: ActionActor[] }
>;

const AUTOMATION_PPL_TO_ADD_LABELS: Record<ActionWithPplToAdd['type'], string> = {
  [AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION]:
    'Referenced in calendar invite automation',
  [AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION]:
    'Referenced in calendar invite automation',
  [AutomationActionType.GOOGLE_ADD_TO_GROUP]:
    'Referenced in Add to Group automation',
  [AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP]:
    'Referenced in Add to Group automation',
  [AutomationActionType.OKTA_ADD_TO_GROUP]:
    'Referenced in Add to Group automation',
  [AutomationActionType.SLACK_INVITE_TO_CHANNEL]:
    'Referenced in Add to Channel automation',
  [AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL]:
    'Referenced in Add to Channel automation',
  [AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT]:
    'Referenced in Lessonly automation',
  [AutomationActionType.DOCEBO_ENROLL]: 'Referenced in Docebo automation'
};

const hasPplToAdd = (
  action: AutomationActionTemplate
): action is ActionWithPplToAdd => 'pplToAdd' in action;

const findAutomationActionUsage = (
  action: AutomationActionTemplate,
  actorKey: string
): string | null => {
  if (hasPplToAdd(action)) {
    return action.pplToAdd.some((a) => actorActorMatches(a, actorKey))
      ? AUTOMATION_PPL_TO_ADD_LABELS[action.type]
      : null;
  }
  switch (action.type) {
    case AutomationActionType.ASANA_CREATE_TASK:
      return actorActorMatches(action.assignee, actorKey) ||
        action.collaborators.some((a) => actorActorMatches(a, actorKey))
        ? 'Referenced in Asana ticket'
        : null;
    case AutomationActionType.ATLASSIAN_CREATE_ISSUE:
      return actorActorMatches(action.reporter, actorKey) ||
        action.fields.some(
          (f) =>
            (f.type === 'USER' && actorActorMatches(f.value, actorKey)) ||
            (f.type === 'USERLIST' &&
              f.value.some((a) => actorActorMatches(a, actorKey)))
        )
        ? 'Referenced in Jira ticket'
        : null;
    case AutomationActionType.ENROLL_IN_EXPERIENCE:
      return action.actorToEnroll === actorKey
        ? 'Referenced in Enroll in Experience automation'
        : null;
    case AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE:
      return action.pplToShareWith.some((a) => actorActorMatches(a, actorKey))
        ? 'Document share list'
        : null;
    // HTTP_POST, SYNC_TO_DATA_DESTINATION and WORKDAY_TRIGGER_BUSINESS_PROCESS
    // (always targets the Participant) have no actor reference.
    case AutomationActionType.HTTP_POST:
    case AutomationActionType.SYNC_TO_DATA_DESTINATION:
    case AutomationActionType.WORKDAY_TRIGGER_BUSINESS_PROCESS:
      return null;
    default:
      return assertNever(action);
  }
};

// Shared by tasks and messages: both can be the assignee, both can have
// receivesCopy roles, and both can reference {{actorKey.field}} in content.
const findAssigneeUsages = (
  item: { actor: string; blocks: TaskBlockTemplate[] },
  itemType: 'TASK' | 'MESSAGE',
  itemTitle: string,
  actorKey: string
): ActorUsageRef[] => {
  const usages: ActorUsageRef[] = [];
  if (item.actor === actorKey) {
    usages.push({ itemType, itemTitle, detail: 'Assignee' });
  }
  if (hasReceivesCopyRole(item, actorKey)) {
    usages.push({ itemType, itemTitle, detail: 'Receives a copy' });
  }
  if (findContentVariableUsage(item.blocks, actorKey)) {
    usages.push({ itemType: 'CONTENT', itemTitle, detail: 'Referenced in content' });
  }
  return usages;
};

/**
 * Finds every place in a template's own definitions that reference a
 * supporting actor by key: as a task/message's assignee, a "receives a
 * copy" role, a subtask role (calendar attendee, DocuSign signer/CC), an
 * automation action's role field, or a `{{actorKey.field}}` content
 * variable. Used to block archiving/removing an actor that's still in use,
 * and to list the offending items to the admin.
 */
export const findActorUsages = (
  tpl: IExperienceTemplate,
  actorKey: string
): ActorUsageRef[] => {
  const usages: ActorUsageRef[] = [];
  const messages = getAllMessageTemplates(tpl.messages);

  for (const task of tpl.tasks) {
    usages.push(...findAssigneeUsages(task, 'TASK', task.title, actorKey));
    usages.push(...findTaskSubtaskUsages(tpl, task, actorKey));
  }

  for (const message of messages) {
    usages.push(
      ...findAssigneeUsages(message, 'MESSAGE', messageTitle(message), actorKey)
    );
  }

  for (const automation of tpl.automations) {
    for (const action of automation.actions) {
      const detail = findAutomationActionUsage(action, actorKey);
      if (detail) {
        usages.push({
          itemType: 'AUTOMATION',
          itemTitle: automation.title,
          detail
        });
      }
    }
  }

  return usages;
};
